﻿import React, { useState, useEffect, Fragment, useRef } from 'react';
import { FormDateRangeInput, FormMultiSelect } from 'components/form-fields';
import { ListingTable } from "components/listing";
import { PageRestricted } from 'pages/errors/page-restricted';
import { UserAccess, UserHasAccess } from 'components/user-access';
import { AssetService, JobService } from 'services';
import { ShowHide } from "components/layout";
import moment from "moment";
import { Button } from 'components/buttons'
import { DateToLocal } from "components/dates";
import { NotificationToaster } from 'components/notifications';
import { Intent } from '@blueprintjs/core';
import { DataDisplayTable } from "components/data-display";
import { Breadcrumbs } from "components/navigation";
import PropTypes from 'prop-types';
import assetTypeDisplay from "pages/asset/edit/AssetTypeDisplay.json";

export function TyreChecksReconcile({ breadcrumbs }) {

    const defaultRecordSize = 25;
    const [tableSortBy, setTableSortBy] = useState({ headerName: "Date", sortName: "STARTDATE" });
    const [tableSortDir, setTableSortDir] = useState("D");
    const [tableSortByOrder, setTableSortByOrder] = useState({ headerName: "Registration", sortName: "REGISTRATION" });
    const [tableSortDirOrder, setTableSortDirOrder] = useState("A");
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalOrderRecords, setTotalOrderRecords] = useState(0);
    const [tableRows, setTableRows] = useState([]);
    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize, setPageSize] = useState(defaultRecordSize);
    const [pageNumberOrder, setPageNumberOrder] = useState(1);
    const [pageSizeOrder, setPageSizeOrder] = useState(defaultRecordSize);
    const [loadingData, setLoadingData] = useState(true);
    const [loadingOrderData, setLoadingOrderData] = useState(false);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [startDateOrder, setStartDateOrder] = useState(null);
    const [endDateOrder, setEndDateOrder] = useState(null);
    const [selectedJobDetails, setSelectedJobDetails] = useState({});
    const [selectedJobId, setSelectedJobId] = useState(null);
    const [selectedAssetTypes, setSelectedAssetTypes] = useState([]);
    const [orderData, setOrderData] = useState([]);
    const [assetTypes, setAssetTypes] = useState([]);
    const [headerSections, setHeaderSections] = useState([]);
    const [headerSections2, setHeaderSections2] = useState([]);


    const tableHeaders = ["Date", "Job Number", "Account", "Asset Type", "Registration", "Chassis Number", "Serial Number"];
    const tableHeadersOrders = ["Date", "Order Number", "Registration", "DETR Number", "Asset Type", "Employee", "Reg Field",
        "Chassis Number", "Serial Number", "Vehicle Number", "VIN", "Licence plate"];

    const sortableHeaders = [
        { headerName: "Date", sortName: "COMPLETEDDATE" },
        { headerName: "Job Number", sortName: "PUBSID" },
        { headerName: "Account", sortName: "ACCOUNTNAME" },
        { headerName: "Asset Type", sortName: "ASSETTYPE" },
        { headerName: "Registration", sortName: "REGISTRATION" },
        { headerName: "Chassis Number", sortName: "CHASSISNUMBER" },
        { headerName: "Serial Number", sortName: "SERIALNUMBER" }
    ];

    const sortableHeadersOrders = [
        { headerName: "Date", sortName: "COMPLETEDDATE" },
        { headerName: "Order Number", sortName: "JOBID" },
        { headerName: "Registration", sortName: "REGISTRATION" },
        { headerName: "DETR Number", sortName: "DETRNUMBER" },
        { headerName: "Asset Type", sortName: "ASSETTYPEDESCRIPTION" },
        { headerName: "Chassis Number", sortName: "CHASSISNUMBER" },
        { headerName: "Serial Number", sortName: "SERIALNUMBER" },
        { headerName: "Employee", sortName: "EMPLOYEENAME" },
        { headerName: "Reg Field", sortName: "REGISTRATIONFIELD" },
        { headerName: "Vehicle Number", sortName: "VEHICLENUMBER" },
        { headerName: "VIN", sortName: "VIN" },
        { headerName: "Licence plate", sortName: "LICENCEPLATE" }

    ];
    const requiredAction = "Internal";
    const canAccessPage = UserHasAccess(requiredAction);

    const currentPageNumber = useRef();
    const currentPageSize = useRef();
    const currentStartDate = useRef();
    const currentEndDate = useRef();
    const currentTableSortBy = useRef();
    const currentTableSortDir = useRef();
    const currentOrderStartDate = useRef();
    const currentOrderEndDate = useRef();
    const currentOrderAccountId = useRef();
    const currentOrderAssetTypes = useRef();
    const currentOrderPageNumber = useRef();
    const currentOrderPageSize = useRef();
    const currentOrderSortBy = useRef();
    const currentOrderSortDir = useRef();
    const orderRequestSequence = useRef(0);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setInitialDates();
        initialLoad();
    }, []);


    function initialLoad() {
        AssetService.getAssetOptions(true)
            .then(assetOptions => {
                setAssetTypes(assetOptions.assetTypes || []);
            })
            .catch(() => {
                setAssetTypes([]);
            })
    }

    function fetchTableData(forceRefresh) {

        if (canAccessPage) {

            if (currentPageNumber.current === pageNumber &&
                currentPageSize.current === pageSize &&
                currentStartDate.current === startDate &&
                currentEndDate.current === endDate &&
                currentTableSortBy.current === tableSortBy &&
                currentTableSortDir.current === tableSortDir &&
                !forceRefresh
            ) {
                //If none of the values have changed, then 2 have been updated at once, so prevent the listing from performing another call.
                return;
            }

            currentPageNumber.current = pageNumber;
            currentPageSize.current = pageSize;
            currentStartDate.current = startDate;
            currentEndDate.current = endDate;
            currentTableSortBy.current = tableSortBy;
            currentTableSortDir.current = tableSortDir;

            setLoadingData(true);

            const mappedFilters = [
                {
                    key: "StartDate",
                    value: startDate,
                },
                {
                    key: "EndDate",
                    value: endDate,
                },
                {
                    key: "MissingTireData",
                    value: 'true',
                },

            ];

            const jobSearchRequest = {
                requestCount: pageSize,
                pageNumber: pageNumber,
                sortField: tableSortBy.sortName,
                sortDirection: tableSortDir,
                filters: mappedFilters
            };

            JobService.getTireTaskJobs(jobSearchRequest).then(r => {
                let data = [];

                if (r?.data != null) {
                    data = r.data.map((d) => {
                        return [
                            <DateToLocal key={`date-${d.pubsId}`} format='DD/MM/YYYY'>{d.completedDate}</DateToLocal>,
                            d.pubsId,
                            `${d.accountName} ${d.membershipNumber}`,
                            d.assetType,
                            d.registration,
                            d.chassisNumber,
                            d.serialNumber,
                            <div key={d.pubsId} className="button-row-small">
                                <Button onClick={() => selectJobProductAsset(d.pubsId, d.completedDate, d.assetId, d.assetTypeId, d.accountName, d.assetType, d.registration, d.accountId, d.detrNumber)} icon="tick" iconOnly={true} text="Select job to match" large={false} minimal={true}></Button>
                            </div>
                        ];
                    })
                }

                setTableRows(data);
                setTotalRecords(r.totalCount);
                setLoadingData(false);

            }, function () {
                NotificationToaster.show(Intent.DANGER, "Could not retrieve completed jobs, please try again");
            });
        }
    }

    function getOrderData(stDate, eDate, accountId, assetTypeIds) {
        //The endpoint takes a DateOnly, and the bounds are compared against the picked range, so any
        //time component has to be dropped before either is used.
        const fromDate = moment(stDate).format("YYYY-MM-DD");
        const toDate = moment(eDate).format("YYYY-MM-DD");

        const orderSearchRequest = {
            requestCount: pageSizeOrder,
            pageNumber: pageNumberOrder,
            sortField: tableSortByOrder.sortName,
            sortDirection: tableSortDirOrder,
            filters: [],
            startDate: fromDate,
            endDate: toDate,
            accountId: accountId,
            assetTypeIds: assetTypeIds
        };
        //Clearing the orders empties the listing while the fetch is in flight, so the previous job's
        //rows cannot be matched against the newly selected one.
        setOrderData([]);
        setTotalOrderRecords(0);
        setLoadingOrderData(true);
        //Selecting a job, sorting, filtering and paging each start a fetch, so only the newest may
        //write - an earlier response arriving late would otherwise replace the newer orders and drop the
        //loading state while that fetch is still running.
        orderRequestSequence.current += 1;
        const requestSequence = orderRequestSequence.current;

        AssetService.getTireTaskCompletedOrders(orderSearchRequest).then(r => {

            if (requestSequence !== orderRequestSequence.current) {
                return;
            }

            if (r?.data != null) {
                setOrderData(r.data);
                setTotalOrderRecords(r.totalCount);
            }
            setLoadingOrderData(false);
        }, function () {
            if (requestSequence !== orderRequestSequence.current) {
                return;
            }

            NotificationToaster.show(Intent.DANGER, "Could not retrieve order data, please try again");
            setLoadingOrderData(false);
        });
    }
    function setHeaders(jobNumber, completedDate, registration, assetType, accountName, assetTypeId, detrNumber) {

        setHeaderSections([
            {
                header: "Job Number",
                value: jobNumber,
                key: 'job-id'
            },
            {
                header: "Account Name",
                value: accountName,
                key: 'account-name'
            },
            {
                header: "Completed Date",
                value: <DateToLocal format="DD/MM/YYYY">
                    {completedDate}
                </DateToLocal>,
                key: 'completed-date'
            }]);
        const assetSections = [{
            header: "Registration",
            value: registration,
            key: 'reg-id'
        },
            {
                header: "Asset Type",
                value: assetType,
                key: 'asset-type'
            }];

        if (assetTypeHasDetrNumber(assetTypeId)) {
            assetSections.push({
                header: "DETR Number",
                value: detrNumber,
                key: 'detr-number'
            });
        }

        setHeaderSections2(assetSections);
    };

    //Follows the same asset type display rules as the asset pages.
    function assetTypeHasDetrNumber(assetTypeId) {
        return assetTypeDisplay.find(display => display.assetTypeId === assetTypeId)?.displayDetrNumber === true;
    }

    useEffect(() => {
        if (startDate != null && endDate != null) {
            fetchTableData(false);
        }
    }, [pageSize, pageNumber, tableSortBy, tableSortDir]);

    useEffect(() => {
        if (startDate != null && endDate != null) {
            fetchTableData(true);
        }
    }, [startDate, endDate]);


    useEffect(() => {
        //The orders are only searched once a job is chosen - the account it belongs to is part of the search.
        if (selectedJobDetails.accountId == null || startDateOrder == null || endDateOrder == null) {
            return;
        }

        const assetTypeIds = selectedAssetTypes.map(assetType => assetType.id);
        //The asset types are a new array on every change, so they are compared by their ids rather than
        //by reference.
        const assetTypeKey = [...assetTypeIds].sort((first, second) => first - second).join(",");

        if (currentOrderStartDate.current === startDateOrder &&
            currentOrderEndDate.current === endDateOrder &&
            currentOrderAccountId.current === selectedJobDetails.accountId &&
            currentOrderAssetTypes.current === assetTypeKey &&
            currentOrderPageNumber.current === pageNumberOrder &&
            currentOrderPageSize.current === pageSizeOrder &&
            currentOrderSortBy.current === tableSortByOrder &&
            currentOrderSortDir.current === tableSortDirOrder
        ) {
            //If none of the criteria have changed, then more than one has been updated at once, so prevent the listing from performing another call.
            return;
        }

        currentOrderStartDate.current = startDateOrder;
        currentOrderEndDate.current = endDateOrder;
        currentOrderAccountId.current = selectedJobDetails.accountId;
        currentOrderAssetTypes.current = assetTypeKey;
        currentOrderPageNumber.current = pageNumberOrder;
        currentOrderPageSize.current = pageSizeOrder;
        currentOrderSortBy.current = tableSortByOrder;
        currentOrderSortDir.current = tableSortDirOrder;

        getOrderData(startDateOrder, endDateOrder, selectedJobDetails.accountId, assetTypeIds);
    }, [startDateOrder, endDateOrder, selectedAssetTypes, selectedJobDetails.accountId, pageNumberOrder, pageSizeOrder, tableSortByOrder, tableSortDirOrder]);

    function selectJobProductAsset(jobId, completedDate, assetId, assetTypeId, accountName, assetypeDescription, registration, accountId, detrNumber) {
        setSelectedJobId(jobId);
        setSelectedJobDetails(prev => ({
            ...prev,
            jobId,
            completedDate,
            assetId,
            assetTypeId,
            accountName,
            assetypeDescription,
            registration,
            accountId
        }));

        setHeaders(jobId, completedDate, registration, assetypeDescription, accountName, assetTypeId, detrNumber);
        const selections = [];
        selections.push({
            id: assetTypeId
        });

        setSelectedAssetTypes(selections);

        //The search window is seeded to the job's completed date, held as a date with no time because the
        //endpoint takes a DateOnly. Every criterion is set in the one go so the search runs once.
        const seededDate = completedDate == null ? null : moment(completedDate).format("YYYY-MM-DD");
        setStartDateOrder(seededDate);
        setEndDateOrder(seededDate);
        setPageNumberOrder(1);
    }

    function onTableSort(header, direction) {
        setTableSortDir(direction);
        setTableSortBy(header);
    }

    function onTableSortOrder(header, direction) {
        setTableSortDirOrder(direction);
        setTableSortByOrder(header);        
    }
    function onPagingChange(newPageNumber, newPageSize) {
        setPageNumber(newPageNumber);
        setPageSize(newPageSize);
    }

    function onPagingChangeOrder(newPageNumberOrder, newPageSizeOrder) {
        setPageNumberOrder(newPageNumberOrder);
        setPageSizeOrder(newPageSizeOrder);
    }

    function onDateRangeChange(item) {
        if ((item?.startDate?.date != null && item?.endDate?.date != null)) {
            setPageNumber(1);
            setStartDate(item.startDate?.dateString);
            setEndDate(item.endDate?.dateString);
        }
    }

    function onOrderDateRangeChange(item) {
        setPageNumberOrder(1);
        setStartDateOrder(item.startDate?.dateString);
        setEndDateOrder(item.endDate?.dateString);
    }

    function selectMatchedOrder(orderId, accountId) {
        setSaving(true);
        AssetService.updateTireTaskChangeAsset(orderId, selectedJobDetails.assetId, selectedJobDetails.jobId, accountId)
            .then(() => {
                NotificationToaster.show(Intent.SUCCESS, `Tyre task order successfully matched`, false);
                removeMatchedJob();
                setSaving(false);

            })
            .catch((error) => {
                NotificationToaster.show(Intent.DANGER, error, false);
                setSaving(false);
            });
    }

    function removeMatchedJob() {
        const copy = [...tableRows];
        const filteredData = copy.filter(d => d[1] !== selectedJobId);
        setTableRows(filteredData);
        setSelectedJobId(null);
        //Matching changes the orders held against the account, so the next job must search again even
        //when it carries the same criteria as this one.
        currentOrderAccountId.current = null;
    }

    function formatOrderRows(filteredOrderData) {
        let orderRows = [];
        if (filteredOrderData != null) {
            orderRows = filteredOrderData.map((d) => {

                return [
                    <DateToLocal key={`date-${d.jobId}`} format='DD/MM/YYYY'>{d.completedDate}</DateToLocal>,
                    d.orderNumber,
                    d.registration,
                    d.detrNumber,
                    d.assetTypeDescription,
                    d.employeeName,
                    d.registrationField,
                    d.chassisNumber,
                    d.serialNumber,
                    d.vehicleNumber,
                    d.vin,
                    d.licencePlate,
                    <div className="button-row-small" key={d.orderNumber}>
                        <Button onClick={() => selectMatchedOrder(d.orderId, d.accountId)} intent={Intent.PRIMARY} text="Match" large={false} small disabled={saving}></Button>
                    </div>
                ];
            })
        }

        return orderRows;
    }

    function setInitialDates() {
        const defaultWeeksBack = -1;
        setStartDate(moment().add(defaultWeeksBack, 'W').format("YYYY-MM-DD"));
        setEndDate(moment().format("YYYY-MM-DD"));
        setStartDateOrder(moment().add(defaultWeeksBack, 'W').format("YYYY-MM-DD"));
        setEndDateOrder(moment().format("YYYY-MM-DD"));
    }

    function onAssetTypesRemove(_, index) {
        const listClone = [...selectedAssetTypes];
        listClone.splice(index, 1);
        setPageNumberOrder(1);
        setSelectedAssetTypes(listClone);
    }

    function onAssetTypesChange(item) {
        const listClone = [...selectedAssetTypes];

        if (hasValue(item, listClone)) {
            return;
        }

        listClone.push(item);
        setPageNumberOrder(1);
        setSelectedAssetTypes(listClone);
    }

    function hasValue(item, array) {
        if (!array) {
            return false;
        }

        return array.some(function (x) {
            return x.id === item.id;
        });
    }

    function onCancel() {
        setSelectedJobId(null);
    }

    const orderTableRows = formatOrderRows(orderData);

    return (
        <UserAccess perform={requiredAction}
            yes={() => (
                <div className="row">
                    <Breadcrumbs items={breadcrumbs} />
                    <h1>Tire Task Reconciliation</h1>
                    <ShowHide
                        evaluator={selectedJobId != null}
                        hide={(
                            <Fragment>
                                <div className="spacer-bottom">
                                    <div className="filter-controls">
                                        <div className="inline-items">
                                            <FormDateRangeInput
                                                endDate={endDate}
                                                disabled={loadingData}
                                                headingText={""}
                                                large
                                                onChange={onDateRangeChange}
                                                startDate={startDate}
                                                allowClear={false}
                                                loading={loadingData}
                                                maxDate={moment().toDate()}
                                            ></FormDateRangeInput>
                                        </div>
                                    </div>
                                </div>
                                <div className="intro-text"><p>These jobs expect to have tire data but do not. Select the job and see if you can match to data from tire task.</p></div>

                                <ListingTable
                                    id="listing-table-location"
                                    headers={tableHeaders}
                                    data={tableRows}
                                    totalRecordCount={totalRecords}
                                    onPagingChange={onPagingChange}
                                    loadingData={loadingData}
                                    noDataMessage={"No jobs found, please try a different date range."}
                                    startPage={pageNumber}
                                    pageSize={pageSize}
                                    sortable
                                    sortableHeaders={sortableHeaders}
                                    sortedBy={tableSortBy}
                                    sortedDir={tableSortDir}
                                    onSort={onTableSort}
                                    searchTerm={""}
                                />
                            </Fragment>
                        )}
                        show={(
                            <Fragment>
                                <DataDisplayTable
                                    data={headerSections}
                                    displayInRows={false}
                                    loading={loadingData}
                                    columnsPerRow={3}   
                                />
                                <DataDisplayTable
                                    data={headerSections2}
                                    displayInRows={false}
                                    loading={loadingData}
                                    columnsPerRow={3}
                                />
                                <div className="intro-text"><p>This list the tire task orders that you can link to the selected job. If a match is not found then clear the asset type  filter or expand the date range.</p></div>
                                <div className="spacer-bottom">
                                    <div className="filter-controls">
                                        <div className="inline-items">
                                            <FormDateRangeInput
                                                endDate={endDateOrder}
                                                disabled={loadingData || loadingOrderData || saving}
                                                headingText={""}
                                                large
                                                onChange={onOrderDateRangeChange}
                                                startDate={startDateOrder}
                                                allowClear={false}
                                                maxDate={moment().toDate()}
                                            ></FormDateRangeInput>
                                            <FormMultiSelect
                                                id="asset-types"
                                                headingText=""
                                                items={assetTypes}
                                                onItemSelect={onAssetTypesChange}
                                                selectedValues={selectedAssetTypes}
                                                placeholder="Select asset types"
                                                onRemove={onAssetTypesRemove}
                                                disabled={saving}
                                            />
                                            <Button
                                                text="Back"
                                                onClick={onCancel}
                                                intent={Intent.PRIMARY}
                                                disabled={saving}
                                            ></Button>
                                        </div>
                                    </div>
                                </div>

                                <ListingTable
                                    id="order-listing-table-location"
                                    headers={tableHeadersOrders}
                                    data={orderTableRows}
                                    totalRecordCount={totalOrderRecords}
                                    onPagingChange={onPagingChangeOrder}
                                    loadingData={loadingOrderData}
                                    noDataMessage={"No orders found, please try a different date range."}
                                    startPage={pageNumberOrder}
                                    pageSize={pageSizeOrder}
                                    sortable
                                    sortableHeaders={sortableHeadersOrders}
                                    sortedBy={tableSortByOrder}
                                    sortedDir={tableSortDirOrder}
                                    onSort={onTableSortOrder}
                                    searchTerm={""}
                                />
                            </Fragment>
                        )}
                    />
                </div>
            )}
            no={() => (
                <PageRestricted />
            )}
        />
    );
}

TyreChecksReconcile.propTypes = {
    breadcrumbs: PropTypes.array.isRequired,
};